"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-[12px] font-default font-medium text-foreground uppercase tracking-widest transition-colors hover:text-primary relative after:absolute after:bottom-[-4px] after:left-0 after:h-[1px] after:w-0 after:bg-primary after:transition-all hover:after:w-full" style={{
            fontWeight: 500
          }}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  setOpen
}: {
  setOpen: (open: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-28 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-default font-semibold text-foreground hover:text-primary transition-colors uppercase tracking-widest" onClick={() => setOpen(false)}>
          {item.label}
        </Link>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="252276" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 border-b bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-border/50 shadow-lg" : "bg-background border-transparent"}`}>
      <div className="container mx-auto px-4 md:px-6 h-32 flex items-center justify-between">
        {}
        <Link to="/" className="flex items-center shrink-0">
          <WvcLogo className="text-primary !w-[100px] !h-auto [&_svg]:text-primary [&_svg]:!w-[100px] [&_svg]:!h-auto" />
        </Link>

        {}
        <div className="hidden md:flex items-center gap-8 lg:gap-12">
          <MenuProvider menu_id="3">
            <DesktopMenu />
          </MenuProvider>
          
          <Link to="https://wa.me/5511966187866" newTab={true}>
            <Button className="bg-transparent text-primary border border-primary/30 hover:bg-primary/10 font-default font-semibold px-8 tracking-widest uppercase text-xs transition-all duration-300">SOLICITAR CONEXÃO</Button>
          </Link>
        </div>

        {}
        <div className="md:hidden flex items-center">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/10">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] border-l border-border bg-background/95 backdrop-blur-xl">
              <SheetHeader className="border-b border-border/20 pb-4">
                <SheetTitle className="text-left">
                  <WvcLogo className="h-20 w-auto text-primary" />
                </SheetTitle>
              </SheetHeader>
              
              <MenuProvider menu_id="3">
                <MobileMenu setOpen={setMobileMenuOpen} />
              </MenuProvider>
              
              <div className="mt-8 flex flex-col gap-4">
                <Link to="https://wa.me/5511966187866" newTab={true}>
                  <Button className="w-full bg-transparent text-primary border border-primary/30 hover:bg-primary/10 font-default font-semibold tracking-widest uppercase text-xs" onClick={() => setMobileMenuOpen(false)}>
                    Consultoria
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}